import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import { articleMap, getHotArticles } from '@/mocks/articles';

export default function Article() {
  const { id = 'a1' } = useParams<{ id: string }>();
  const article = articleMap[id];
  const relatedArticles = getHotArticles(4).filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="bg-background-50 min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabels: Record<string, string> = {
    football: '足球',
    basketball: '篮球',
    tennis: '网球',
    esports: '电竞',
    others: '综合',
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: 'ky体育',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.ky-sports.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${article.title} - ky体育`}
        description={article.summary}
        keywords={article.tags.join(',') + ',ky体育,ky体育官网,ky体育平台'}
        canonicalUrl={`https://www.ky-sports.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schemaData={[articleSchema]}
      />
      <Navbar />
      <main className="bg-background-50">
        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: categoryLabels[article.category] || article.category, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <span className="inline-block bg-primary-500 text-white text-xs px-3 py-1 rounded-full mb-4">
                  {categoryLabels[article.category] || article.category}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-64 md:h-96 object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_p]:mb-4 [&_p]:leading-loose
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-foreground-950
                  [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-foreground-900
                  [&_strong]:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-14">
                  <h4 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关推荐</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="group cursor-pointer"
                      >
                        <article className="bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300">
                          <div className="relative h-36 overflow-hidden">
                            <img
                              src={related.imageUrl}
                              alt={related.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-4">
                            <h5 className="font-medium text-foreground-950 text-sm line-clamp-2 group-hover:text-primary-500 transition-colors">
                              {related.title}
                            </h5>
                            <p className="mt-1 text-xs text-foreground-400">{related.date}</p>
                          </div>
                        </article>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}